import { useNavigate } from '@tanstack/react-router';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ArrowRight,
  RefreshCw,
  PlusCircle,
  Clock,
  Building2,
  Link2,
  Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { LogStream } from '@/components/progress/LogStream';
import { cn, formatDuration, formatEnumStatus } from '@/lib/utils';
import type { Migration, MigrationLog, MigrationStatus, ModuleConfiguration } from '@/types';

interface CompletionViewProps {
  migration: Migration;
  modulesConfig: ModuleConfiguration[];
  logs?: MigrationLog[] | undefined;
  onRetry?: (() => void) | undefined;
  isRetrying?: boolean | undefined;
}

const statusConfig: Record<MigrationStatus, {
  icon: React.ElementType;
  title: string;
  description: string;
  variant: 'success' | 'warning' | 'destructive';
  iconClass: string;
  bgClass: string;
}> = {
  Succeeded: {
    icon: CheckCircle2,
    title: 'Migration Complete',
    description: 'All modules have been successfully migrated to Amplify.',
    variant: 'success',
    iconClass: 'text-success',
    bgClass: 'bg-success/10',
  },
  Partial: {
    icon: AlertTriangle,
    title: 'Partial Migration',
    description: 'Some modules completed successfully, but others failed.',
    variant: 'warning',
    iconClass: 'text-warning',
    bgClass: 'bg-warning/10',
  },
  Failed: {
    icon: XCircle,
    title: 'Migration Failed',
    description: 'The migration could not be completed due to errors.',
    variant: 'destructive',
    iconClass: 'text-destructive',
    bgClass: 'bg-destructive/10',
  },
  InProgress: {
    icon: Clock,
    title: 'In Progress',
    description: 'Migration is still running.',
    variant: 'success',
    iconClass: 'text-primary',
    bgClass: 'bg-primary/10',
  },
};

export function CompletionView({
  migration,
  modulesConfig,
  logs: logsProp,
  onRetry,
  isRetrying,
}: CompletionViewProps) {
  const navigate = useNavigate();
  const config = statusConfig[migration.status];
  const Icon = config.icon;

  const logs = logsProp ?? [];
  const errorCount = logs.filter((l) => l.level === 'Error').length;
  const warnCount = logs.filter((l) => l.level === 'Warning').length;

  const succeededModules = migration.modules.filter(
    (m) => m.migrateStatus === 'Succeeded' && m.amplifyProvisioningStatus !== 'Failed'
  );
  const partialModules = migration.modules.filter(
    (m) => m.migrateStatus === 'Succeeded' && m.amplifyProvisioningStatus === 'Failed'
  );
  const failedModules = migration.modules.filter((m) => m.migrateStatus === 'Failed');
  const skippedModules = migration.modules.filter((m) => m.migrateStatus === 'Skipped');

  const getModuleName = (moduleKey: string) => {
    return modulesConfig.find((c) => c.moduleKey === moduleKey)?.displayName ?? moduleKey;
  };

  // Modules that never ran — e.g. organization creation failed before any module started —
  // are still Pending on a finished migration. The retry API reprocesses them, so offer it.
  const notRunModules = migration.modules.filter(
    (m) => m.migrateStatus === 'Pending' || m.migrateStatus === 'InProgress'
  );

  const canRetry =
    (failedModules.length > 0 || partialModules.length > 0 || notRunModules.length > 0) && onRetry;

  return (
    <div className="space-y-6">
      {/* Status header */}
      <Card className={cn('border-2', `border-${config.variant}/50`)}>
        <CardContent className="pt-6">
          <div className="flex flex-col items-center text-center">
            <div
              className={cn(
                'flex h-20 w-20 items-center justify-center rounded-full mb-4',
                config.bgClass
              )}
            >
              <Icon className={cn('h-10 w-10', config.iconClass)} />
            </div>
            <h2 className="text-2xl font-bold">{config.title}</h2>
            <p className="text-muted-foreground mt-1">{config.description}</p>
          </div>
        </CardContent>
      </Card>

      {/* Organization, Bridge, and timing info */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Organization
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-medium">{migration.organizationName}</p>
            <p className="text-sm text-muted-foreground">
              {migration.organizationType === 'New' ? 'New Organization' : 'Existing Organization'}
            </p>
            {migration.amplifyOrganizationUuid && (
              <p className="text-xs text-muted-foreground mt-2 font-mono">
                {migration.amplifyOrganizationUuid}
              </p>
            )}
          </CardContent>
        </Card>

        {migration.bridgeEntityUuid && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Link2 className="h-4 w-4" />
                Bridge Entity
              </CardTitle>
            </CardHeader>
            <CardContent>
              {migration.bridgeEntityName && (
                <p className="font-medium">{migration.bridgeEntityName}</p>
              )}
              <p className="text-xs text-muted-foreground font-mono mt-1">
                {migration.bridgeEntityUuid}
              </p>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Duration
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-medium">
              {formatDuration(migration.startedAt, migration.completedAt)}
            </p>
            <p className="text-sm text-muted-foreground">
              Started: {new Date(migration.startedAt).toLocaleString()}
            </p>
            {migration.completedAt && (
              <p className="text-sm text-muted-foreground">
                Completed: {new Date(migration.completedAt).toLocaleString()}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Module results */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Module Results</CardTitle>
          <CardDescription>
            {succeededModules.length} succeeded
            {partialModules.length > 0 && `, ${partialModules.length} partial`}
            {`, ${failedModules.length} failed, ${skippedModules.length} skipped`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {/* Succeeded modules */}
            {succeededModules.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-success mb-2">Succeeded</h4>
                <div className="space-y-2">
                  {succeededModules.map((mod) => (
                    <div
                      key={mod.moduleKey}
                      className="flex items-center justify-between rounded-lg bg-success/5 p-3"
                    >
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-success" />
                        <span>{getModuleName(mod.moduleKey)}</span>
                      </div>
                      <Badge variant="outline" className="text-success">
                        Complete
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Partial modules — migration succeeded but Amplify provisioning failed */}
            {partialModules.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-warning mb-2">Partial</h4>
                <div className="space-y-2">
                  {partialModules.map((mod) => (
                    <div
                      key={mod.moduleKey}
                      className="rounded-lg bg-warning/5 border border-warning/20 p-3"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4 text-warning" />
                          <span>{getModuleName(mod.moduleKey)}</span>
                        </div>
                        <Badge variant="outline" className="border-warning text-warning">
                          Partial
                        </Badge>
                      </div>
                      <p className="text-sm text-warning/80 mt-2 pl-6">
                        Data migrated successfully, but the module could not be added to Amplify.
                        Use Retry Provisioning to attempt again.
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Failed modules */}
            {failedModules.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-destructive mb-2">Failed</h4>
                <div className="space-y-2">
                  {failedModules.map((mod) => (
                    <div
                      key={mod.moduleKey}
                      className="rounded-lg bg-destructive/5 p-3"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <XCircle className="h-4 w-4 text-destructive" />
                          <span>{getModuleName(mod.moduleKey)}</span>
                        </div>
                        <Badge variant="destructive">Failed</Badge>
                      </div>
                      {mod.migrateError && (
                        <p className="text-sm text-destructive/80 mt-2 pl-6">
                          {mod.migrateError}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Skipped modules */}
            {skippedModules.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-2">Skipped</h4>
                <div className="space-y-2">
                  {skippedModules.map((mod) => (
                    <div
                      key={mod.moduleKey}
                      className="flex items-center justify-between rounded-lg bg-muted/50 p-3"
                    >
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                        <span>{getModuleName(mod.moduleKey)}</span>
                      </div>
                      <Badge variant="secondary">Skipped</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* User Migration Results */}
      {migration.userMigrationPhaseStatus !== 'NotApplicable' && (
        <Card>
          <CardHeader className="pb-4 border-b">
            <div className="flex items-center gap-2 mb-1">
              <Users className="h-5 w-5 text-muted-foreground" />
              <CardTitle className="text-lg">User Migration</CardTitle>
              <Badge
                variant={
                  migration.userMigrationPhaseStatus === 'Succeeded'
                    ? 'default'
                    : migration.userMigrationPhaseStatus === 'Partial'
                      ? 'secondary'
                      : 'destructive'
                }
              >
                {formatEnumStatus(migration.userMigrationPhaseStatus)}
              </Badge>
            </div>
            <CardDescription>Phase 2 — users collected, onboarded, and invited to Amplify</CardDescription>
          </CardHeader>

          {/* Headline outcome */}
          <div className="flex items-center gap-5 px-6 py-5 border-b">
            <div className="flex-shrink-0 text-5xl font-bold leading-none text-success">
              {migration.totalOnboardedSuccessfully}
              <span className="text-base font-normal text-muted-foreground"> / {migration.totalUniqueUsers}</span>
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium mb-1">users successfully onboarded to Amplify</p>
              <div className="h-2 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full bg-success transition-all"
                  style={{
                    width: migration.totalUniqueUsers > 0
                      ? `${Math.round((migration.totalOnboardedSuccessfully / migration.totalUniqueUsers) * 100)}%`
                      : '0%',
                  }}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {migration.totalUniqueUsers > 0
                  ? `${Math.round((migration.totalOnboardedSuccessfully / migration.totalUniqueUsers) * 100)}% onboarding rate`
                  : 'No users'}
              </p>
            </div>
          </div>

          {/* Pipeline flow */}
          <div className={cn(
            'grid border-b bg-muted/30 px-6 py-4 text-center',
            migration.totalRawUsersCollected > 0 ? 'grid-cols-4' : 'grid-cols-3',
          )}>
            {migration.totalRawUsersCollected > 0 && (
              <div className="relative">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Collected</p>
                <p className="text-xl font-bold">{migration.totalRawUsersCollected}</p>
                <p className="text-[11px] text-muted-foreground">raw across modules</p>
                <ArrowRight className="absolute -right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              </div>
            )}
            <div className="relative">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Unique</p>
              <p className="text-xl font-bold">{migration.totalUniqueUsers}</p>
              <p className="text-[11px] text-muted-foreground">after dedup</p>
              <ArrowRight className="absolute -right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            </div>
            <div className="relative">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Onboarded</p>
              <p className="text-xl font-bold text-success">{migration.totalOnboardedSuccessfully}</p>
              <p className="text-[11px] text-muted-foreground">to Amplify</p>
              <ArrowRight className="absolute -right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Invited</p>
              <p className="text-xl font-bold text-primary">{migration.totalInvitesSent}</p>
              <p className="text-[11px] text-muted-foreground">
                {migration.totalInvitesPending > 0
                  ? `${migration.totalInvitesPending} queued for batch send`
                  : 'emails sent'}
              </p>
            </div>
          </div>

          {/* Conditional callouts */}
          {(migration.totalOrgOwnerExcluded > 0 || migration.totalInviteFailed > 0) && (
            <div className="px-6 py-4 space-y-3 border-b">
              {migration.totalOrgOwnerExcluded > 0 && (
                <div className="flex items-start gap-3 rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 text-sm">
                  <Users className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-blue-900">
                      {migration.totalOrgOwnerExcluded} user{migration.totalOrgOwnerExcluded !== 1 ? 's' : ''} excluded — organisation owner
                    </p>
                    <p className="text-blue-700 text-xs mt-0.5">
                      The organisation owner does not need to be onboarded. They were automatically detected and skipped (not counted as a failure).
                    </p>
                  </div>
                </div>
              )}
              {migration.totalInviteFailed > 0 && (
                <div className="flex items-start gap-3 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm">
                  <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-amber-900">
                      {migration.totalInviteFailed} welcome invitation{migration.totalInviteFailed !== 1 ? 's' : ''} could not be sent (non-fatal)
                    </p>
                    <p className="text-amber-700 text-xs mt-0.5">
                      These users are onboarded in Amplify but may not have received a welcome email. You can resend invitations manually from Amplify.
                    </p>
                    {migration.inviteFailureCategories && Object.keys(migration.inviteFailureCategories).length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {Object.entries(migration.inviteFailureCategories).map(([category, count]) => (
                          <span
                            key={category}
                            className="text-[11px] px-2 py-0.5 bg-amber-100 border border-amber-300 rounded text-amber-800"
                          >
                            {count} · {category.replace(/-/g, ' ')}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Per-module breakdown */}
          {migration.modules.some((m) => m.userMigrationStatus !== 'NotConfigured' && m.userMigrationStatus !== 'Disabled') && (
            <div className="px-6 py-4">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Per-module breakdown</h4>
              <div className="space-y-2">
                {(['Succeeded', 'Partial', 'Failed'] as const).map((groupStatus) => {
                  const groupModules = migration.modules.filter(
                    (m) => m.userMigrationStatus === groupStatus,
                  );
                  if (groupModules.length === 0) return null;
                  return (
                    <div key={groupStatus}>
                      <p className={cn(
                        'text-[11px] font-semibold uppercase tracking-wider mb-1.5',
                        groupStatus === 'Succeeded' ? 'text-success' : groupStatus === 'Partial' ? 'text-warning' : 'text-destructive',
                      )}>
                        {groupStatus}
                      </p>
                      <div className="space-y-1.5">
                        {groupModules.map((mod) => (
                          <div
                            key={mod.moduleKey}
                            className={cn(
                              'flex items-center gap-3 rounded-lg px-3 py-2.5',
                              groupStatus === 'Succeeded'
                                ? 'bg-success/5 border border-success/20'
                                : groupStatus === 'Partial'
                                  ? 'bg-warning/5 border border-warning/20'
                                  : 'bg-destructive/5 border border-destructive/20',
                            )}
                          >
                            {groupStatus === 'Succeeded' ? (
                              <CheckCircle2 className="h-4 w-4 text-success flex-shrink-0" />
                            ) : groupStatus === 'Partial' ? (
                              <AlertTriangle className="h-4 w-4 text-warning flex-shrink-0" />
                            ) : (
                              <XCircle className="h-4 w-4 text-destructive flex-shrink-0" />
                            )}
                            <span className="text-sm font-medium flex-1">{getModuleName(mod.moduleKey)}</span>
                            <span className="flex items-center gap-1 text-xs text-muted-foreground">
                              <span className="font-semibold text-foreground">{mod.userMigrationTotalUsers}</span>
                              <span>users</span>
                              <ArrowRight className="h-3 w-3 text-muted-foreground/50" />
                              <span className="font-semibold text-foreground">{mod.userMigrationUuidsSent}</span>
                              <span>UUIDs synced</span>
                              {mod.userMigrationUuidsFailed > 0 && (
                                <span className="font-semibold text-destructive ml-1">· {mod.userMigrationUuidsFailed} failed</span>
                              )}
                            </span>
                            <Badge
                              variant={
                                mod.userMigrationStatus === 'Succeeded'
                                  ? 'default'
                                  : mod.userMigrationStatus === 'Partial'
                                    ? 'secondary'
                                    : 'destructive'
                              }
                              className="ml-2"
                            >
                              {formatEnumStatus(mod.userMigrationStatus)}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Migration Logs */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold">Migration Logs</CardTitle>
            <div className="flex items-center gap-2">
              {errorCount > 0 && (
                <Badge variant="destructive">{errorCount} error{errorCount !== 1 ? 's' : ''}</Badge>
              )}
              {warnCount > 0 && (
                <Badge variant="outline" className="border-warning text-warning">
                  {warnCount} warning{warnCount !== 1 ? 's' : ''}
                </Badge>
              )}
              <Badge variant="secondary">{logs.length} entries</Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              No log entries recorded for this migration.
            </p>
          ) : (
            <LogStream
              logs={logs}
              moduleKeys={migration.modules.map((m) => m.moduleKey)}
              maxHeight="400px"
            />
          )}
        </CardContent>
      </Card>

      {/* Action buttons */}
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        {canRetry && (
          <Button
            variant="outline"
            onClick={onRetry}
            disabled={isRetrying}
            className="gap-2"
          >
            {isRetrying ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            {failedModules.length > 0 && partialModules.length > 0
              ? 'Retry Failed & Partial Modules'
              : partialModules.length > 0
                ? 'Retry Partial Modules'
                : failedModules.length > 0
                  ? 'Retry Failed Modules'
                  : 'Retry Migration'}
          </Button>
        )}

        <Button
          variant="outline"
          onClick={() => navigate({ to: '/' })}
          className="gap-2"
        >
          <ArrowRight className="h-4 w-4" />
          View All Migrations
        </Button>

        <Button onClick={() => navigate({ to: '/migrations/new' })} className="gap-2">
          <PlusCircle className="h-4 w-4" />
          Start New Migration
        </Button>
      </div>
    </div>
  );
}
